import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Button,
    Grid,
    Paper,
    List,
    ListItem,
    ListItemText,
    Typography,
    Box,
    TextField,
    FormControlLabel,
    Checkbox,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton
} from '@mui/material';
import { ExpandMore, ExpandLess, ErrorOutline } from '@mui/icons-material';
import { getPackageRatesByPackageIds, updateBulkPackageRates } from '../../../Services/Packages.service';
import { useRateConstraints, validatePerKmCharge } from '../../../hooks/useRateConstraints';
import { showAlert } from '../../../Services/Shared.service';
import DatePicker from "react-datepicker";
import { addSeconds } from "date-fns";
import moment from "moment";

const BulkEditForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const packages = location.state?.packages || [];
    const source = location.state?.source || null; // 'packages' or 'corp'

    // State to store packages with their fetched rates
    const [packagesWithRates, setPackagesWithRates] = useState([]);
    // State to track currently selected package in the sidebar
    const [selectedPackageId, setSelectedPackageId] = useState(null);
    // State for validation errors: { [pkgId]: { [rateIndex]: { fieldName: errorMessage } } }
    const [validationErrors, setValidationErrors] = useState({});
    // State for confirmation dialog
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    // State to track expanded rows in the rate table
    const [expandedRows, setExpandedRows] = useState({});
    // State to track if user has made any edits
    const [hasEdits, setHasEdits] = useState(false);
    // State to track if user has valid access
    const [isValidAccess, setIsValidAccess] = useState(false);
    // Reward constraints for validation
    const rewardConstraints = useRateConstraints();


    // Toggle row expansion
    const toggleRowExpansion = (rateIndex) => {
        setExpandedRows(prev => ({
            ...prev,
            [rateIndex]: !prev[rateIndex]
        }));
    };

    // Helper to get the appropriate return path based on access source
    const getReturnPath = () => {
        // First check navigation state (most reliable)
        if (source === 'corp') {
            return `${process.env.PUBLIC_URL}/tariff_master/view/corp`;
        }
        if (source === 'packages') {
            return `${process.env.PUBLIC_URL}/tariff_master/view/packages`;
        }
        return -1;
    };

    // Redirect guard: check for valid access via sessionStorage
    useEffect(() => {
        const hasPackageAccess = sessionStorage.getItem('packageBulkEditAccess');
        const hasCorpAccess = sessionStorage.getItem('corpBulkEditAccess');
        
        if (!hasPackageAccess && !hasCorpAccess) {
            console.log('No valid access, redirecting to appropriate tab');
            // No valid access, redirect to appropriate tab
            navigate(getReturnPath(), { replace: true });
        } else {
            // Valid access, allow component to render
            setIsValidAccess(true);
        }
    }, [navigate]);

    // Validate a single rate and return errors object
    const validateRate = (rate) => {
        const errors = {};
        
        if (!rate.minPkgDistance && rate.minPkgDistance !== 0) {
            errors.minPkgDistance = 'Minimum Distance is required';
        } else if (Number(rate.minPkgDistance) < 0) {
            errors.minPkgDistance = 'Minimum Distance cannot be negative';
        }
        
        if (!rate.maxPkgDistance && rate.maxPkgDistance !== 0) {
            errors.maxPkgDistance = 'Maximum Distance is required';
        } else if (Number(rate.maxPkgDistance) < 0) {
            errors.maxPkgDistance = 'Maximum Distance cannot be negative';
        }
        
        if (!rate.pkgCharge && rate.pkgCharge !== 0) {
            errors.pkgCharge = 'Package Fee is required';
        } else if (Number(rate.pkgCharge) < 0) {
            errors.pkgCharge = 'Package Fee cannot be negative';
        }
        
        if (!rate.addnlPerKmCharge && rate.addnlPerKmCharge !== 0) {
            errors.addnlPerKmCharge = 'Per Km Charge is required';
        } else if (Number(rate.addnlPerKmCharge) < 0) {
            errors.addnlPerKmCharge = 'Per Km Charge cannot be negative';
        } else {
            const result = validatePerKmCharge(rate.addnlPerKmCharge, rewardConstraints);
            if (result !== true) errors.addnlPerKmCharge = result;
        }
        
        // Time validation only if isTimeBased is true
        if (rate.isTimeBased && rate.timeFrom && rate.timeTo) {
            const timeFromSeconds = rate.timeFrom.getHours() * 3600 + rate.timeFrom.getMinutes() * 60 + rate.timeFrom.getSeconds();
            let timeToSeconds = rate.timeTo.getHours() * 3600 + rate.timeTo.getMinutes() * 60 + rate.timeTo.getSeconds();
            // Treat 12:00 AM (0 seconds) as end of day (86400 seconds) for Time To
            if (timeToSeconds === 0) {
                timeToSeconds = 86400;
            }
            if (timeToSeconds <= timeFromSeconds) {
                errors.timeTo = 'End time should be greater than start time';
            }
        }
        
        // Min/Max distance comparison (only check if both are valid non-negative)
        if (Number(rate.minPkgDistance) >= 0 && Number(rate.maxPkgDistance) >= 0 && 
            rate.minPkgDistance !== '' && rate.maxPkgDistance !== '' &&
            Number(rate.minPkgDistance) >= Number(rate.maxPkgDistance)) {
            errors.maxPkgDistance = 'Maximum Distance should be greater than Minimum Distance';
        }
        
        return errors;
    };

    useEffect(() => {
        // Only fetch rates if we have valid access and packages
        if (!isValidAccess || packages.length === 0) return;
        
        const packageIds = packages.map(pkg => pkg.id);
        getPackageRatesByPackageIds(packageIds)
            .then(response => {
                const mappedPackages = packages.map(pkg => {
                    const rates = response.data.filter(rate => rate.packagesId === pkg.id).map(rate => ({
                        ...rate,
                        timeFrom: (rate.timeFrom !== null && rate.timeFrom !== undefined) ? addSeconds(new Date().setHours(0, 0, 0, 0), rate.timeFrom) : null,
                        timeTo: (rate.timeTo !== null && rate.timeTo !== undefined) ? addSeconds(new Date().setHours(0, 0, 0, 0), rate.timeTo) : null
                    }));
                    return { ...pkg, packageRates: rates };
                });
                setPackagesWithRates(mappedPackages);
                // Select the first package by default
                if (mappedPackages.length > 0) {
                    setSelectedPackageId(mappedPackages[0].id);
                }
                console.log('Selected Package Rates for Bulk Edit:', packages);
            })
            .catch(error => {
                console.error('Error fetching package rates:', error);
            });
    }, [packages, isValidAccess]);

    // Show confirmation dialog when cancel is clicked (only if there are edits)
    const handleCancelClick = () => {
        if (hasEdits) {
            setShowConfirmDialog(true);
        } else {
            // No edits, navigate directly without confirmation
            const returnPath = getReturnPath();
            sessionStorage.removeItem('packageBulkEditAccess');
            sessionStorage.removeItem('corpBulkEditAccess');
            navigate(returnPath);
        }
    };

    // Confirm leaving - navigate back
    const handleConfirmLeave = () => {
        setShowConfirmDialog(false);
        const returnPath = getReturnPath();
        sessionStorage.removeItem('packageBulkEditAccess');
        sessionStorage.removeItem('corpBulkEditAccess');
        navigate(returnPath);
    };

    // Cancel leaving - stay on page
    const handleCancelLeave = () => {
        setShowConfirmDialog(false);
    };

    const handleSave = () => {
        // Validate all rates before saving
        let allValid = true;
        const newErrors = {};
        
        packagesWithRates.forEach(pkg => {
            newErrors[pkg.id] = {};
            pkg.packageRates.forEach((rate, index) => {
                const rateErrors = validateRate(rate);
                newErrors[pkg.id][index] = rateErrors;
                if (Object.keys(rateErrors).length > 0) {
                    allValid = false;
                }
            });
        });
        
        setValidationErrors(newErrors);
        
        if (!allValid) {
            console.error('Validation errors found, cannot save.');
            showAlert('error', 'Validation errors found, cannot save.');
            return;
        }
        
        const allRates = packagesWithRates.flatMap(pkg => pkg.packageRates.map(rate => {
            // If isTimeBased is false, set timeFrom to 0 and timeTo to 86400
            if (!rate.isTimeBased) {
                return {
                    ...rate,
            pkgTimeInMin: rate.pkgTimeInMin ? Number(rate.pkgTimeInMin) : 0,
                    pkgDistance: rate.maxPkgDistance,
                    packageValue: rate.pkgCharge,
                    timeFrom: 0,
                    timeTo: 86400
                };
            }
            
            // Calculate time values for time-based rates
            const timeFromSeconds = rate.timeFrom ? Number(moment.duration(moment(rate.timeFrom).format("HH:mm:ss")).asSeconds()) : 0;
            let timeToSeconds = rate.timeTo ? Number(moment.duration(moment(rate.timeTo).format("HH:mm:ss")).asSeconds()) : 86400;
            
            // If timeTo is 12:00 AM (0 seconds), treat it as end of day (86400)
            if (timeToSeconds === 0) {
                timeToSeconds = 86400;
            }
            
            return {
                ...rate,
                pkgDistance: rate.maxPkgDistance,
                packageValue: rate.pkgCharge,
                timeFrom: timeFromSeconds,
                timeTo: timeToSeconds
            };
        }));

        updateBulkPackageRates(allRates)
            .then(response => {
                if (response.status === 200) {
                    showAlert('success', 'Package rates updated successfully');
                    const returnPath = getReturnPath();
                    sessionStorage.removeItem('packageBulkEditAccess');
                    sessionStorage.removeItem('corpBulkEditAccess');
                    navigate(returnPath);
                }
            })
            .catch(error => {
                showAlert('error', 'Failed to update package rates');
                console.error("Error updating bulk package rates:", error);
            });
    };

    const handleRateChange = (pkgId, rateIndex, field, value) => {
        // Mark that user has made edits
        setHasEdits(true);
        
        setPackagesWithRates(prevPackages =>
            prevPackages.map(pkg => {
                if (pkg.id === pkgId) {
                    const newRates = [...pkg.packageRates];
                    newRates[rateIndex] = { ...newRates[rateIndex], [field]: value };
                    
                    // Validate the updated rate immediately
                    const rateErrors = validateRate(newRates[rateIndex]);
                    setValidationErrors(prev => ({
                        ...prev,
                        [pkgId]: {
                            ...prev[pkgId],
                            [rateIndex]: rateErrors
                        }
                    }));
                    
                    return { ...pkg, packageRates: newRates };
                }
                return pkg;
            })
        );
    };

    // Find the currently selected package object
    const selectedPackage = packagesWithRates.find(p => p.id === selectedPackageId);

    // Helper to get error for a specific field in a rate
    const getFieldError = (pkgId, rateIndex, fieldName) => {
        return validationErrors[pkgId]?.[rateIndex]?.[fieldName] || '';
    };

    // Helper to check if a package has any validation errors
    const hasPackageErrors = (pkgId) => {
        const pkgErrors = validationErrors[pkgId];
        if (!pkgErrors) return false;
        return Object.values(pkgErrors).some(rateErrors => 
            Object.keys(rateErrors).length > 0
        );
    };

    return (
        <Box sx={{ padding: '20px', height: '85vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Edit Package Rates</Typography>
                <Box>
                    <Button
                        variant="outlined"
                        onClick={handleCancelClick}
                        sx={{ mr: 1, border: '1px solid rgba(219, 57, 57, 1)', color: 'rgba(219, 57, 57, 1)', '&:hover': { borderColor: '#b71c1c', backgroundColor: '#ffebee' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!hasEdits}
                        sx={{
                            backgroundColor: hasEdits ? 'rgba(57, 181, 74, 1)' : 'rgba(189, 189, 189, 1)',
                            '&:hover': { backgroundColor: hasEdits ? 'rgba(57, 181, 74, 1)' : 'rgba(189, 189, 189, 1)' },
                            '&.Mui-disabled': { 
                                backgroundColor: 'rgba(189, 189, 189, 1)',
                                color: 'rgba(255, 255, 255, 0.7)'
                            }
                        }}
                    >
                        Save
                    </Button>
                </Box>
            </Box>

            {/* Main Content Area */}
            <Paper sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                {/* Sidebar - Package List */}
                <Box sx={{ 
                    width: '20%', 
                    flexShrink: 0, 
                    borderRight: '1px solid #e0e0e0', 
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#f5f5f5' 
                }}>
                    <Typography variant="subtitle1" sx={{
                        p: 2, fontWeight: 'bold', borderBottom: '1px solid rgba(202, 202, 202, 1)', bgcolor: 'rgba(225, 244, 228, 1)',
                        flexShrink: 0
                    }}>
                        Packages
                    </Typography>
                    <Box sx={{ 
                        overflowY: 'auto', 
                        flexGrow: 1,
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none'
                    }}>
                        <List disablePadding>
                            {packagesWithRates.map((pkg) => (
                                <ListItem
                                    button
                                    key={pkg.id}
                                    selected={selectedPackageId === pkg.id}
                                    onClick={() => setSelectedPackageId(pkg.id)}
                                    sx={{
                                        '&.Mui-selected': { borderRight: '4px solid #39B54A' },
                                        borderBottom: '1px solid rgba(217, 217, 217, 1)',
                                        bgcolor: '#fff !important',
                                        px: '8%',
                                        py: '5%',
                                        minHeight: '10%',
                                        width: '100%'
                                    }}
                                >
                                    <ListItemText primary={pkg.name} />
                                    {hasPackageErrors(pkg.id) && (
                                        <ErrorOutline 
                                            sx={{ 
                                                color: '#d32f2f', 
                                                fontSize: '20px',
                                                ml: 1
                                            }} 
                                        />
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Box>

                {/* Main Edit Area */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, '&::-webkit-scrollbar': { display: 'none' } }}>
                    {selectedPackage ? (
                        <>
                            <Typography variant="h6" sx={{ mb: 3, color: '#39B54A' }}>
                                {selectedPackage.name} – Rates
                            </Typography>

                            {selectedPackage.packageRates && selectedPackage.packageRates.length > 0 ? (
                                <Table sx={{ border: '1px solid rgba(217, 217, 217, 1)', borderRadius: '8px' }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'rgba(225, 244, 228, 1)' }}>
                                            <TableCell sx={{ fontWeight: 'bold', width: '50px' }}></TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Min Distance</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Max Distance</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Package Fee</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Per Km Charge</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Time Based</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Time From</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Time To</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedPackage.packageRates.map((rate, index) => (
                                            <React.Fragment key={index}>
                                                <TableRow 
                                                    onClick={() => toggleRowExpansion(index)}
                                                    sx={{ 
                                                        cursor: 'pointer', 
                                                        '&:hover': { bgcolor: '#f9f9f9' },
                                                        bgcolor: expandedRows[index] ? 'rgba(225, 244, 228, 0.5)' : 'inherit'
                                                    }}
                                                >
                                                    <TableCell>
                                                        <IconButton size="small">
                                                            {expandedRows[index] ? <ExpandLess /> : <ExpandMore />}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>{rate.minPkgDistance ?? '-'}</TableCell>
                                                    <TableCell>{rate.maxPkgDistance ?? '-'}</TableCell>
                                                    <TableCell>{rate.pkgCharge ?? '-'}</TableCell>
                                                    <TableCell>{rate.addnlPerKmCharge ?? '-'}</TableCell>
                                                    <TableCell>{rate.isTimeBased ? 'Yes' : 'No'}</TableCell>
                                                    <TableCell>{rate.isTimeBased && rate.timeFrom ? moment(rate.timeFrom).format('h:mm A') : '-'}</TableCell>
                                                    <TableCell>{rate.isTimeBased && rate.timeTo ? moment(rate.timeTo).format('h:mm A') : '-'}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedRows[index] ? '1px solid rgba(217, 217, 217, 1)' : 'none' }}>
                                                        <Collapse in={expandedRows[index]} timeout={300}>
                                                            <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                                                                <Grid container spacing={2}>
                                                                    <Grid item xs={12} sm={6} md={3}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Minimum Distance"
                                                                            type="number"
                                                                            value={rate.minPkgDistance ?? ''}
                                                                            onChange={(e) => e.target.value.length <= 6 && handleRateChange(selectedPackage.id, index, 'minPkgDistance', e.target.value)}
                                                                            onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                                                            size="small"
                                                                            error={!!getFieldError(selectedPackage.id, index, 'minPkgDistance')}
                                                                            helperText={getFieldError(selectedPackage.id, index, 'minPkgDistance')}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={6} md={3}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Maximum Distance"
                                                                            type="number"
                                                                            value={rate.maxPkgDistance ?? ''}
                                                                            onChange={(e) => e.target.value.length <= 6 && handleRateChange(selectedPackage.id, index, 'maxPkgDistance', e.target.value)}
                                                                            onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                                                            size="small"
                                                                            error={!!getFieldError(selectedPackage.id, index, 'maxPkgDistance')}
                                                                            helperText={getFieldError(selectedPackage.id, index, 'maxPkgDistance')}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={6} md={3}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Package Fee"
                                                                            type="number"
                                                                            value={rate.pkgCharge ?? ''}
                                                                            onChange={(e) => e.target.value.length <= 7 && handleRateChange(selectedPackage.id, index, 'pkgCharge', e.target.value)}
                                                                            onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                                                            size="small"
                                                                            error={!!getFieldError(selectedPackage.id, index, 'pkgCharge')}
                                                                            helperText={getFieldError(selectedPackage.id, index, 'pkgCharge')}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={6} md={3}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Per Km Charge"
                                                                            type="number"
                                                                            value={rate.addnlPerKmCharge ?? ''}
                                                                            onChange={(e) => e.target.value.length <= 6 && handleRateChange(selectedPackage.id, index, 'addnlPerKmCharge', e.target.value)}
                                                                            onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                                                            size="small"
                                                                            error={!!getFieldError(selectedPackage.id, index, 'addnlPerKmCharge')}
                                                                            helperText={getFieldError(selectedPackage.id, index, 'addnlPerKmCharge')}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12}>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={rate.isTimeBased || false}
                                                                                    onChange={(e) => handleRateChange(selectedPackage.id, index, 'isTimeBased', e.target.checked)}
                                                                                    color="success"
                                                                                />
                                                                            }
                                                                            label="Time Based"
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12}>
                                                                        <Collapse in={rate.isTimeBased} timeout={300}>
                                                                            <Grid container spacing={2}>
                                                                                <Grid item xs={12} sm={6}>
                                                                                    <div style={{ marginBottom: '5px', fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)' }}>
                                                                                        Time From (Hours)
                                                                                    </div>
                                                                                    <DatePicker
                                                                                        className="form-control datetimepicker-input digits"
                                                                                        selected={rate.timeFrom}
                                                                                        onChange={(date) =>
                                                                                            handleRateChange(selectedPackage.id, index, 'timeFrom', date)
                                                                                        }
                                                                                        showTimeSelect
                                                                                        showTimeSelectOnly
                                                                                        timeIntervals={60}
                                                                                        timeCaption="Time"
                                                                                        dateFormat="h:mm aa"
                                                                                        timeFormat="h:mm aa"
                                                                                        onKeyDown={(e) => e.preventDefault()}
                                                                                        placeholderText="Select Time From"
                                                                                    />
                                                                                </Grid>

                                                                                <Grid item xs={12} sm={6}>
                                                                                    <div style={{ marginBottom: '5px', fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)' }}>
                                                                                        Time To (Hours)
                                                                                    </div>
                                                                                    <DatePicker
                                                                                        className="form-control datetimepicker-input digits"
                                                                                        selected={rate.timeTo}
                                                                                        onChange={(date) =>
                                                                                            handleRateChange(selectedPackage.id, index, 'timeTo', date)
                                                                                        }
                                                                                        showTimeSelect
                                                                                        showTimeSelectOnly
                                                                                        timeIntervals={60}
                                                                                        timeCaption="Time"
                                                                                        dateFormat="h:mm aa"
                                                                                        timeFormat="h:mm aa"
                                                                                        onKeyDown={(e) => e.preventDefault()}
                                                                                        placeholderText="Select Time To"
                                                                                    />
                                                                                    {getFieldError(selectedPackage.id, index, 'timeTo') && (
                                                                                        <span style={{ color: 'red', fontSize: '12px' }}>
                                                                                            {getFieldError(selectedPackage.id, index, 'timeTo')}
                                                                                        </span>
                                                                                    )}
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Collapse>
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Typography variant="body1" color="textSecondary">No rates found for this package.</Typography>
                            )}
                        </>
                    ) : (
                        <Typography variant="body1" sx={{ mt: 5, textAlign: 'center', color: '#888' }}>
                            Select a package from the list to edit rates.
                        </Typography>
                    )}
                </Box>
            </Paper>

            {/* Confirmation Dialog */}
            <Dialog
                open={showConfirmDialog}
                onClose={handleCancelLeave}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">
                    Discard Changes?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        You have unsaved changes. Are you sure you want to leave? All your changes will be lost.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handleConfirmLeave}
                        variant="contained"
                        sx={{
                            backgroundColor: 'rgba(219, 57, 57, 1)',
                            '&:hover': { backgroundColor: '#b71c1c' }
                        }}
                    >
                        Leave
                    </Button>
                    <Button 
                        onClick={handleCancelLeave} 
                        variant="outlined"
                        sx={{ 
                            border: '1px solid rgba(57, 181, 74, 1)', 
                            color: 'rgba(57, 181, 74, 1)',
                            '&:hover': { borderColor: '#41CC7D', backgroundColor: 'rgba(225, 244, 228, 1)' }
                        }}
                    >
                        Stay
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BulkEditForm;